import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, 
  Target, 
  ClipboardList, 
  CheckCircle, 
  Circle,
  Phone,
  Mail,
  User,
  Calendar,
  FileText,
  AlertTriangle,
  GraduationCap,
  Users,
  MessageSquare,
  ChevronRight,
  Star,
  Clock,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  CalendarPlus,
  CheckCircle2,
  CircleDot,
  Sparkles,
  TrendingUp,
  Award,
  Upload,
  Download,
  File,
  FileCheck,
  FileX,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    nieuw: 'bg-slate-100 text-slate-600',
    bezig: 'bg-blue-100 text-blue-700',
    afgerond: 'bg-green-100 text-green-700',
    gepland: 'bg-amber-100 text-amber-700',
    voltooid: 'bg-green-100 text-green-700',
    geannuleerd: 'bg-red-100 text-red-700',
    // Opdracht statussen
    ingediend: 'bg-blue-100 text-blue-700',
    in_beoordeling: 'bg-amber-100 text-amber-700',
    goedgekeurd: 'bg-green-100 text-green-700',
    afgekeurd: 'bg-red-100 text-red-700'
  };
  
  const labels = {
    nieuw: 'Nieuw',
    bezig: 'Bezig',
    afgerond: 'Afgerond',
    gepland: 'Gepland',
    voltooid: 'Voltooid',
    geannuleerd: 'Geannuleerd',
    ingediend: 'Ingediend',
    in_beoordeling: 'In beoordeling',
    goedgekeurd: 'Goedgekeurd',
    afgekeurd: 'Afgekeurd'
  };
  
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${styles[status] || styles.nieuw}`}>
      {labels[status] || status}
    </span>
  );
};

export default function StagiaireDashboard({ user, token, onOpenChat }) {
  const [inwerkData, setInwerkData] = useState(null);
  const [activeTab, setActiveTab] = useState('overzicht');
  const [checklist, setChecklist] = useState([]);
  const [leerdoelen, setLeerdoelen] = useState([]);
  const [gesprekken, setGesprekken] = useState([]);
  const [voortgang, setVoortgang] = useState(null);
  const [opdrachten, setOpdrachten] = useState([]);
  const [rooster, setRooster] = useState([]);
  const [bpvProtocol, setBpvProtocol] = useState(null);
  
  // Urenregistratie state
  const [urenRegistraties, setUrenRegistraties] = useState([]);
  const [totaalUren, setTotaalUren] = useState(0);
  const [showAddUren, setShowAddUren] = useState(false);
  const [newUren, setNewUren] = useState({
    datum: new Date().toISOString().split('T')[0],
    start_tijd: '09:00',
    eind_tijd: '17:00',
    pauze_minuten: 30,
    locatie: 'De Zwaluw',
    werkzaamheden: '',
    opmerkingen: ''
  });
  
  // Ziekmeldingen state
  const [ziekmeldingen, setZiekmeldingen] = useState([]);
  const [showAddZiekmelding, setShowAddZiekmelding] = useState(false);
  const [newZiekmelding, setNewZiekmelding] = useState({
    start_datum: new Date().toISOString().split('T')[0],
    verwachte_eind_datum: '',
    reden: '',
    contact_opgenomen: false
  });
  
  // Evaluaties state
  const [evaluaties, setEvaluaties] = useState([]);
  
  // Modals
  const [showAddLeerdoel, setShowAddLeerdoel] = useState(false);
  const [showEditLeerdoel, setShowEditLeerdoel] = useState(null);
  const [showAddGesprek, setShowAddGesprek] = useState(false);
  const [showEditGesprek, setShowEditGesprek] = useState(null);
  const [showUploadOpdracht, setShowUploadOpdracht] = useState(false);
  const [showBpvProtocol, setShowBpvProtocol] = useState(false);
  
  // Form states
  const [newLeerdoel, setNewLeerdoel] = useState({ doel: '', categorie: 'algemeen' });
  const [editLeerdoel, setEditLeerdoel] = useState({ doel: '', status: '', reflectie: '', bewijs: '' });
  const [newGesprek, setNewGesprek] = useState({ datum: '', tijd: '', type: 'voortgang', met_wie: '' });
  const [newOpdracht, setNewOpdracht] = useState({ titel: '', beschrijving: '', bestand: null });
  const [uploadLoading, setUploadLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    await Promise.all([
      fetchInwerkData(),
      fetchLeerdoelen(),
      fetchGesprekken(),
      fetchVoortgang(),
      fetchOpdrachten(),
      fetchRooster(),
      fetchUrenRegistraties(),
      fetchZiekmeldingen(),
      fetchEvaluaties()
    ]);
  };

  // Urenregistratie functies
  const fetchUrenRegistraties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/dagregistratie`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUrenRegistraties(response.data.uren || []);
      setTotaalUren(response.data.totaal_uren || 0);
    } catch (error) {
      console.error('Error fetching uren:', error);
    }
  };

  const handleAddUren = async () => {
    try {
      await axios.post(`${API_URL}/api/stagiaire/dagregistratie`, newUren, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Uren geregistreerd!');
      setShowAddUren(false);
      setNewUren({
        datum: new Date().toISOString().split('T')[0],
        start_tijd: '09:00',
        eind_tijd: '17:00',
        pauze_minuten: 30,
        locatie: 'De Zwaluw',
        werkzaamheden: '',
        opmerkingen: ''
      });
      fetchUrenRegistraties();
    } catch (error) {
      toast.error('Fout bij registreren uren');
    }
  };

  const handleDeleteUren = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze registratie wilt verwijderen?')) return;
    try {
      await axios.delete(`${API_URL}/api/stagiaire/dagregistratie/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Registratie verwijderd');
      fetchUrenRegistraties();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Ziekmeldingen functies
  const fetchZiekmeldingen = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/ziekmeldingen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setZiekmeldingen(response.data.ziekmeldingen || []);
    } catch (error) {
      console.error('Error fetching ziekmeldingen:', error);
    }
  };

  const handleAddZiekmelding = async () => {
    try {
      await axios.post(`${API_URL}/api/stagiaire/ziekmeldingen`, newZiekmelding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ziekmelding ingediend');
      setShowAddZiekmelding(false);
      setNewZiekmelding({
        start_datum: new Date().toISOString().split('T')[0],
        verwachte_eind_datum: '',
        reden: '',
        contact_opgenomen: false
      });
      fetchZiekmeldingen();
    } catch (error) {
      toast.error('Fout bij indienen ziekmelding');
    }
  };

  const handleHerstelmelding = async (id) => {
    try {
      await axios.put(`${API_URL}/api/stagiaire/ziekmeldingen/${id}/herstel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Hersteld gemeld!');
      fetchZiekmeldingen();
    } catch (error) {
      toast.error('Fout bij herstelmelding');
    }
  };

  // Evaluaties functies
  const fetchEvaluaties = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/evaluaties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvaluaties(response.data.evaluaties || []);
    } catch (error) {
      console.error('Error fetching evaluaties:', error);
    }
  };

  const fetchInwerkData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/inwerk`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInwerkData(response.data);
      setChecklist(response.data.checklist_eerste_dag || []);
      setBpvProtocol(response.data.bpv_protocol || null);
    } catch (error) {
      console.error('Error fetching inwerk data:', error);
    }
  };

  const fetchRooster = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/rooster/week`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooster(response.data || []);
    } catch (error) {
      console.error('Error fetching rooster:', error);
    }
  };

  const fetchLeerdoelen = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/leerdoelen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeerdoelen(response.data);
    } catch (error) {
      console.error('Error fetching leerdoelen:', error);
    }
  };

  const fetchGesprekken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/gesprekken`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGesprekken(response.data);
    } catch (error) {
      console.error('Error fetching gesprekken:', error);
    }
  };

  const fetchVoortgang = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/voortgang`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVoortgang(response.data);
    } catch (error) {
      console.error('Error fetching voortgang:', error);
    }
  };

  const fetchOpdrachten = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stagiaire/opdrachten`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpdrachten(response.data);
    } catch (error) {
      console.error('Error fetching opdrachten:', error);
    }
  };

  // Leerdoelen CRUD
  const handleAddLeerdoel = async () => {
    if (!newLeerdoel.doel.trim()) {
      toast.error('Vul een leerdoel in');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/stagiaire/leerdoelen`, newLeerdoel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leerdoel toegevoegd!');
      setShowAddLeerdoel(false);
      setNewLeerdoel({ doel: '', categorie: 'algemeen' });
      fetchLeerdoelen();
      fetchVoortgang();
    } catch (error) {
      toast.error('Fout bij toevoegen leerdoel');
    }
  };

  const handleUpdateLeerdoel = async () => {
    try {
      await axios.put(`${API_URL}/api/stagiaire/leerdoelen/${showEditLeerdoel}`, editLeerdoel, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leerdoel bijgewerkt!');
      setShowEditLeerdoel(null);
      fetchLeerdoelen();
      fetchVoortgang();
    } catch (error) {
      toast.error('Fout bij bijwerken leerdoel');
    }
  };

  const handleUploadBewijs = async (leerdoelId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('bestand', file);
    
    try {
      await axios.post(`${API_URL}/api/stagiaire/leerdoelen/${leerdoelId}/bewijs`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Bewijs geüpload!');
      fetchLeerdoelen();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij uploaden bewijs');
    }
  };

  const handleDownloadBewijs = async (leerdoel) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/stagiaire/leerdoelen/${leerdoel.id}/bewijs`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', leerdoel.bewijs_naam);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fout bij downloaden bewijs');
    }
  };

  const handleDeleteBewijs = async (leerdoelId) => {
    if (!confirm('Weet je zeker dat je dit bewijs wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stagiaire/leerdoelen/${leerdoelId}/bewijs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bewijs verwijderd');
      fetchLeerdoelen();
    } catch (error) {
      toast.error('Fout bij verwijderen bewijs');
    }
  };

  const handleDeleteLeerdoel = async (id) => {
    if (!confirm('Weet je zeker dat je dit leerdoel wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stagiaire/leerdoelen/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leerdoel verwijderd');
      fetchLeerdoelen();
      fetchVoortgang();
    } catch (error) {
      toast.error('Fout bij verwijderen leerdoel');
    }
  };

  const handleQuickStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/stagiaire/leerdoelen/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchLeerdoelen();
      fetchVoortgang();
      toast.success(`Status gewijzigd naar ${newStatus}`);
    } catch (error) {
      toast.error('Fout bij wijzigen status');
    }
  };

  // Gesprekken CRUD
  const handleAddGesprek = async () => {
    if (!newGesprek.datum || !newGesprek.tijd || !newGesprek.met_wie) {
      toast.error('Vul alle velden in');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/stagiaire/gesprekken`, newGesprek, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gesprek gepland!');
      setShowAddGesprek(false);
      setNewGesprek({ datum: '', tijd: '', type: 'voortgang', met_wie: '' });
      fetchGesprekken();
    } catch (error) {
      toast.error('Fout bij plannen gesprek');
    }
  };

  const handleUpdateGesprek = async (gesprekData) => {
    try {
      await axios.put(`${API_URL}/api/stagiaire/gesprekken/${showEditGesprek}`, gesprekData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gesprek bijgewerkt!');
      setShowEditGesprek(null);
      fetchGesprekken();
    } catch (error) {
      toast.error('Fout bij bijwerken gesprek');
    }
  };

  const handleDeleteGesprek = async (id) => {
    if (!confirm('Weet je zeker dat je dit gesprek wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stagiaire/gesprekken/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Gesprek verwijderd');
      fetchGesprekken();
    } catch (error) {
      toast.error('Fout bij verwijderen gesprek');
    }
  };

  // Opdrachten CRUD
  const handleUploadOpdracht = async () => {
    if (!newOpdracht.titel.trim()) {
      toast.error('Vul een titel in');
      return;
    }
    if (!newOpdracht.bestand) {
      toast.error('Selecteer een bestand');
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('titel', newOpdracht.titel);
      formData.append('beschrijving', newOpdracht.beschrijving || '');
      formData.append('bestand', newOpdracht.bestand);

      await axios.post(`${API_URL}/api/stagiaire/opdrachten`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Opdracht ingediend!');
      setShowUploadOpdracht(false);
      setNewOpdracht({ titel: '', beschrijving: '', bestand: null });
      fetchOpdrachten();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij uploaden opdracht');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteOpdracht = async (id) => {
    if (!confirm('Weet je zeker dat je deze opdracht wilt verwijderen?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/stagiaire/opdrachten/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opdracht verwijderd');
      fetchOpdrachten();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij verwijderen opdracht');
    }
  };

  const downloadOpdracht = async (opdracht) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/stagiaire/opdrachten/${opdracht.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', opdracht.bestand_naam);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Fout bij downloaden bestand');
    }
  };

  const toggleChecklistItem = (id) => {
    setChecklist(prev => 
      prev.map(item => 
        item.id === id ? { ...item, done: !item.done } : item
      )
    );
  };

  const completedTasks = checklist.filter(item => item.done).length;
  const totalTasks = checklist.length;
  const checklistProgress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const overallProgress = voortgang?.overall_progress || 0;

  if (!inwerkData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overzicht', name: 'Overzicht', icon: BookOpen },
    { id: 'uren', name: 'Urenregistratie', icon: Clock },
    { id: 'leerdoelen', name: 'BPV Plan', icon: Target },
    { id: 'opdrachten', name: 'Opdrachten', icon: FileText },
    { id: 'rooster', name: 'Rooster', icon: Calendar },
    { id: 'evaluaties', name: 'Evaluaties', icon: Star },
    { id: 'ziekmeldingen', name: 'Ziekmeldingen', icon: AlertTriangle },
    { id: 'gesprekken', name: 'Gesprekken', icon: MessageSquare },
    { id: 'protocol', name: 'BPV Protocol', icon: ClipboardList },
    { id: 'contacten', name: 'Contacten', icon: Users },
  ];

  const categorieOptions = [
    { value: 'algemeen', label: 'Algemeen' },
    { value: 'zorgvaardigheden', label: 'Zorgvaardigheden' },
    { value: 'medicatie', label: 'Medicatie' },
    { value: 'communicatie', label: 'Communicatie' },
    { value: 'administratie', label: 'Administratie' },
    { value: 'samenwerking', label: 'Samenwerking' },
  ];


  const gesprekTypes = [
    { value: 'intake', label: 'Intakegesprek' },
    { value: 'voortgang', label: 'Voortgangsgesprek' },
    { value: 'tussentijds', label: 'Tussentijdse evaluatie' },
    { value: 'eind', label: 'Eindbeoordeling' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome Banner with Progress */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white shadow-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="w-8 h-8" />
              <h1 className="text-2xl font-bold">Welkom, {user?.name?.split(' ')[0]}! 🎓</h1>
            </div>
            <p className="text-purple-100 text-lg">Jouw BPV-voortgang bij Zorg-Vleugel</p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <Target className="w-4 h-4" />
                {voortgang?.leerdoelen?.afgerond || 0}/{voortgang?.leerdoelen?.totaal || 0} leerdoelen
              </span>
              <span className="flex items-center gap-1 bg-white/20 px-3 py-1 rounded-full">
                <Calendar className="w-4 h-4" />
                {voortgang?.gesprekken?.gepland || 0} gesprekken gepland
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold">{overallProgress}%</div>
            <div className="text-purple-200 text-sm">Stage voortgang</div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="h-3 bg-white/20 rounded-full overflow-hidden">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-purple-200">
            <span>{voortgang?.leerdoelen?.bezig || 0} leerdoelen in behandeling</span>
            <span>{voortgang?.leerdoelen?.nieuw || 0} nog te starten</span>
          </div>
        </div>
      </motion.div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => setShowAddLeerdoel(true)}
          className="bg-white p-4 rounded-xl border border-slate-200 hover:border-purple-300 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-purple-200 transition-colors">
            <Plus className="w-5 h-5 text-purple-600" />
          </div>
          <h3 className="font-semibold text-slate-800">Nieuw Leerdoel</h3>
          <p className="text-xs text-slate-500">Voeg toe aan BPV</p>
        </button>
        
        <button
          onClick={() => setShowAddGesprek(true)}
          className="bg-white p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-200 transition-colors">
            <CalendarPlus className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="font-semibold text-slate-800">Plan Gesprek</h3>
          <p className="text-xs text-slate-500">Voortgang bespreken</p>
        </button>
        
        <button
          onClick={() => setActiveTab('leerdoelen')}
          className="bg-white p-4 rounded-xl border border-slate-200 hover:border-green-300 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-green-200 transition-colors">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="font-semibold text-slate-800">Bekijk Voortgang</h3>
          <p className="text-xs text-slate-500">{voortgang?.leerdoelen?.afgerond || 0} afgerond</p>
        </button>
        
        <button
          onClick={onOpenChat}
          className="bg-white p-4 rounded-xl border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all text-left group"
        >
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-orange-200 transition-colors">
            <MessageSquare className="w-5 h-5 text-orange-600" />
          </div>
          <h3 className="font-semibold text-slate-800">Vraag Sahbi AI</h3>
          <p className="text-xs text-slate-500">Stage hulp</p>
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[100px] px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overzicht Tab */}
          {activeTab === 'overzicht' && (
            <div className="space-y-6">
              {/* Checklist Eerste Dag */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Checklist Eerste Dag
                  </h3>
                  <span className="text-sm text-slate-500">{completedTasks}/{totalTasks}</span>
                </div>
                <div className="space-y-2">
                  {checklist.slice(0, 5).map((item) => (
                    <button
                      key={item.id}
                      onClick={() => toggleChecklistItem(item.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        item.done 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-slate-50 border border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {item.done ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <Circle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      )}
                      <span className={`text-sm text-left ${item.done ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                        {item.taak}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Belangrijke Regels */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Belangrijke Regels
                </h3>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <ul className="space-y-2">
                    {inwerkData.belangrijke_regels?.slice(0, 4).map((regel) => (
                      <li key={regel} className="flex items-start gap-2 text-sm text-amber-800">
                        <span className="text-amber-500 mt-0.5">•</span>
                        {regel}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Aankomende Gesprekken */}
              {gesprekken.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    Aankomende Gesprekken
                  </h3>
                  <div className="space-y-2">
                    {gesprekken.filter(g => g.status === 'gepland').slice(0, 3).map((gesprek) => (
                      <div key={gesprek.id} className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-blue-900">{gesprekTypes.find(t => t.value === gesprek.type)?.label || gesprek.type}</div>
                          <div className="text-sm text-blue-700">{gesprek.datum} om {gesprek.tijd} - {gesprek.met_wie}</div>
                        </div>
                        <StatusBadge status={gesprek.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Leerdoelen / BPV Plan Tab */}
          {activeTab === 'leerdoelen' && (
            <div className="space-y-6">
              {/* Progress Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-slate-600">{voortgang?.leerdoelen?.nieuw || 0}</div>
                  <div className="text-xs text-slate-500">Nieuw</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{voortgang?.leerdoelen?.bezig || 0}</div>
                  <div className="text-xs text-blue-600">Bezig</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{voortgang?.leerdoelen?.afgerond || 0}</div>
                  <div className="text-xs text-green-600">Afgerond</div>
                </div>
              </div>

              {/* Add Button */}
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Mijn Leerdoelen</h3>
                <Button
                  onClick={() => setShowAddLeerdoel(true)}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nieuw Leerdoel
                </Button>
              </div>

              {/* Leerdoelen List */}
              <div className="space-y-3">
                {leerdoelen.map((doel) => (
                  <motion.div 
                    key={doel.id}
                    layout
                    className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {doel.status === 'afgerond' ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : doel.status === 'bezig' ? (
                            <CircleDot className="w-5 h-5 text-blue-500" />
                          ) : (
                            <Circle className="w-5 h-5 text-slate-400" />
                          )}
                          <span className="font-medium text-slate-800">{doel.doel}</span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                            {categorieOptions.find(c => c.value === doel.categorie)?.label || doel.categorie}
                          </span>
                          <StatusBadge status={doel.status} />
                        </div>
                        {doel.reflectie && (
                          <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                            💭 {doel.reflectie}
                          </p>
                        )}
                        
                        {/* Bewijs sectie */}
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          {doel.bewijs_naam ? (
                            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-2">
                              <div className="flex items-center gap-2">
                                <FileCheck className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-700 font-medium">{doel.bewijs_naam}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => handleDownloadBewijs(doel)}
                                  className="p-1.5 hover:bg-green-100 rounded text-green-600"
                                  title="Download bewijs"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteBewijs(doel.id)}
                                  className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                  title="Verwijder bewijs"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer hover:text-purple-600 transition-colors">
                              <Upload className="w-4 h-4" />
                              <span>Bewijs uploaden (foto, video, PDF)</span>
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi,.webm,image/*,video/*,application/pdf"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleUploadBewijs(doel.id, e.target.files[0]);
                                  }
                                }}
                                className="hidden"
                              />
                            </label>
                          )}
                          
                          {/* Werkbegeleider feedback sectie */}
                          {doel.wb_feedback && (
                            <div className={`mt-2 p-2 rounded-lg ${doel.status === 'goedgekeurd' ? 'bg-green-50 border border-green-200' : doel.status === 'afgekeurd' ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200'}`}>
                              <p className="text-xs font-medium text-slate-600 mb-1">
                                Feedback van {doel.beoordeeld_door_naam || 'werkbegeleider'}:
                              </p>
                              <p className="text-sm text-slate-700">{doel.wb_feedback}</p>
                              {doel.wb_bewijs_naam && (
                                <div className="flex items-center gap-2 mt-2 text-sm">
                                  <File className="w-4 h-4 text-slate-500" />
                                  <span className="text-slate-600">{doel.wb_bewijs_naam}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Quick status buttons */}
                        {doel.status !== 'afgerond' && (
                          <button
                            onClick={() => handleQuickStatusChange(doel.id, doel.status === 'nieuw' ? 'bezig' : 'afgerond')}
                            className="p-2 hover:bg-green-50 rounded-lg text-green-600 transition-colors"
                            title={doel.status === 'nieuw' ? 'Start' : 'Afronden'}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setEditLeerdoel({
                              doel: doel.doel,
                              status: doel.status,
                              reflectie: doel.reflectie || '',
                              bewijs: doel.bewijs || ''
                            });
                            setShowEditLeerdoel(doel.id);
                          }}
                          className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteLeerdoel(doel.id)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Opdrachten Tab */}
          {activeTab === 'opdrachten' && (
            <div className="space-y-6">
              {/* Header met upload button */}
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Mijn Opdrachten</h3>
                <Button
                  onClick={() => setShowUploadOpdracht(true)}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  Opdracht Uploaden
                </Button>
              </div>

              {/* Opdrachten Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {opdrachten.filter(o => o.status === 'ingediend').length}
                  </div>
                  <div className="text-xs text-blue-600">Ingediend</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">
                    {opdrachten.filter(o => o.status === 'in_beoordeling').length}
                  </div>
                  <div className="text-xs text-amber-600">In Beoordeling</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {opdrachten.filter(o => o.status === 'goedgekeurd').length}
                  </div>
                  <div className="text-xs text-green-600">Goedgekeurd</div>
                </div>
                <div className="bg-red-50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {opdrachten.filter(o => o.status === 'afgekeurd').length}
                  </div>
                  <div className="text-xs text-red-600">Feedback Nodig</div>
                </div>
              </div>

              {/* Opdrachten Lijst */}
              {opdrachten.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Nog geen opdrachten ingediend</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Klik op "Opdracht Uploaden" om je eerste opdracht in te dienen
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {opdrachten.map((opdracht) => (
                    <motion.div
                      key={opdracht.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`p-2 rounded-lg ${
                            opdracht.status === 'goedgekeurd' ? 'bg-green-100' :
                            opdracht.status === 'afgekeurd' ? 'bg-red-100' :
                            opdracht.status === 'in_beoordeling' ? 'bg-amber-100' :
                            'bg-blue-100'
                          }`}>
                            {opdracht.status === 'goedgekeurd' ? (
                              <FileCheck className="w-5 h-5 text-green-600" />
                            ) : opdracht.status === 'afgekeurd' ? (
                              <FileX className="w-5 h-5 text-red-600" />
                            ) : (
                              <File className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-800 truncate">{opdracht.titel}</h4>
                            {opdracht.beschrijving && (
                              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{opdracht.beschrijving}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                              <span>{opdracht.bestand_naam}</span>
                              <span>•</span>
                              <span>{new Date(opdracht.created_at).toLocaleDateString('nl-NL')}</span>
                            </div>
                            
                            {/* Feedback sectie */}
                            {opdracht.feedback && (
                              <div className={`mt-3 p-3 rounded-lg ${
                                opdracht.status === 'goedgekeurd' ? 'bg-green-50 border border-green-200' :
                                'bg-red-50 border border-red-200'
                              }`}>
                                <p className="text-xs font-medium mb-1 text-slate-600">
                                  Feedback van {opdracht.beoordeeld_door_naam || 'werkbegeleider'}:
                                </p>
                                <p className="text-sm text-slate-700">{opdracht.feedback}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2">
                          <StatusBadge status={opdracht.status} />
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadOpdracht(opdracht)}
                              title="Download"
                            >
                              <Download className="w-4 h-4 text-slate-400" />
                            </Button>
                            {opdracht.status === 'ingediend' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteOpdracht(opdracht.id)}
                                title="Verwijderen"
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Rooster Tab */}
          {activeTab === 'rooster' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Weekrooster Woongroep</h3>
                <span className="text-sm text-slate-500">Wie werkt er vandaag?</span>
              </div>

              {/* Vandaag */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800">Vandaag</h4>
                    <p className="text-sm text-blue-600">
                      {new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                  </div>
                </div>
                {rooster.filter(r => {
                  const today = new Date().toLocaleDateString('nl-NL', { weekday: 'long' }).toLowerCase();
                  return r.dag?.toLowerCase() === today;
                }).length > 0 ? (
                  <div className="space-y-2">
                    {rooster.filter(r => {
                      const today = new Date().toLocaleDateString('nl-NL', { weekday: 'long' }).toLowerCase();
                      return r.dag?.toLowerCase() === today;
                    }).map((shift, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{shift.naam}</p>
                            <p className="text-xs text-slate-500">{shift.functie || 'Begeleider'}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          shift.dienst === 'dagdienst' ? 'bg-amber-100 text-amber-700' :
                          shift.dienst === 'avonddienst' ? 'bg-purple-100 text-purple-700' :
                          'bg-slate-100 text-slate-700'
                        }`}>
                          {shift.dienst === 'dagdienst' ? 'Dag' : 
                           shift.dienst === 'avonddienst' ? 'Avond' : 
                           shift.dienst}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-blue-600 italic">Geen rooster informatie voor vandaag</p>
                )}
              </div>

              {/* Weekoverzicht */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <h4 className="font-semibold text-slate-800">Weekoverzicht</h4>
                </div>
                <div className="divide-y divide-slate-100">
                  {['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag', 'zondag'].map((dag) => {
                    const dagRooster = rooster.filter(r => r.dag?.toLowerCase() === dag);
                    const isToday = new Date().toLocaleDateString('nl-NL', { weekday: 'long' }).toLowerCase() === dag;
                    
                    return (
                      <div key={dag} className={`p-3 ${isToday ? 'bg-blue-50' : ''}`}>
                        <div className="flex items-start gap-4">
                          <div className={`w-20 text-sm font-medium capitalize ${isToday ? 'text-blue-700' : 'text-slate-600'}`}>
                            {dag}
                            {isToday && <span className="block text-xs text-blue-500">Vandaag</span>}
                          </div>
                          <div className="flex-1">
                            {dagRooster.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {dagRooster.map((shift, idx) => (
                                  <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-lg text-sm">
                                    <User className="w-3 h-3 text-slate-500" />
                                    <span className="text-slate-700">{shift.naam}</span>
                                    <span className="text-xs text-slate-400">
                                      ({shift.dienst === 'dagdienst' ? 'D' : shift.dienst === 'avonddienst' ? 'A' : 'N'})
                                    </span>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-sm text-slate-400 italic">Geen diensten</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Contact Info */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800">Belangrijke informatie</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Bij vragen over het rooster of als je je ziek wilt melden, neem contact op met je werkbegeleider of de dienstdoende begeleider.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* BPV Protocol Tab */}
          {activeTab === 'protocol' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">BPV Protocol</h3>
              </div>

              {/* Protocol Overview */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <ClipboardList className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-800">Wat is BPV?</h4>
                    <p className="text-sm text-purple-600">Beroepspraktijkvorming - Leren in de praktijk</p>
                  </div>
                </div>
                <p className="text-sm text-slate-700">
                  BPV is een verplicht onderdeel van je MBO-opleiding waarbij je leert in de praktijk. 
                  Dit protocol beschrijft de verantwoordelijkheden van jou, de school, en het leerbedrijf.
                </p>
              </div>

              {/* Jouw Verantwoordelijkheden */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Jouw Verantwoordelijkheden als Stagiaire
                </h4>
                <div className="space-y-3">
                  {[
                    { title: 'Voorbereiding', items: ['Informatie verzamelen over het leerbedrijf', 'Gemotiveerd en goed voorbereid starten'] },
                    { title: 'Tijdens de stage', items: ['Afspraken nakomen uit de praktijkovereenkomst', 'Instructies opvolgen van je praktijkopleider', 'Deelnemen aan contactmomenten met school en werkbegeleider', 'Feedback geven aan je BPV-begeleider'] },
                    { title: 'Beoordeling', items: ['Alle onderdelen van het BPV-programma afronden en inleveren'] },
                    { title: 'Evaluatie', items: ['De BPV-periode evalueren met je begeleider en praktijkopleider'] }
                  ].map((section, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3">
                      <h5 className="font-medium text-slate-700 mb-2">{section.title}</h5>
                      <ul className="space-y-1">
                        {section.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contactmomenten */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                  Contactmomenten (Minimaal 3x per stage)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">1</span>
                    </div>
                    <h5 className="font-medium text-green-800">Startgesprek</h5>
                    <p className="text-xs text-green-600 mt-1">Fysiek op de werkplek</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">2</span>
                    </div>
                    <h5 className="font-medium text-blue-800">Voortgangsgesprek</h5>
                    <p className="text-xs text-blue-600 mt-1">Fysiek of online</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <h5 className="font-medium text-purple-800">Eindgesprek</h5>
                    <p className="text-xs text-purple-600 mt-1">Beoordeling & evaluatie</p>
                  </div>
                </div>
              </div>

              {/* Belangrijke Afspraken */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Belangrijke Afspraken
                </h4>
                <div className="space-y-2">
                  {[
                    'Je hebt recht op een onkostenvergoeding (reiskosten, werkkleding)',
                    'Het leerbedrijf zorgt voor een veilige leer- en werkomgeving',
                    'Je praktijkopleider is gekwalificeerd en toegankelijk',
                    'Bij klachten of problemen meld je dit bij school',
                    'De school en het leerbedrijf evalueren samen jouw voortgang'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg">
                      <Star className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Link naar volledige protocol */}
              <div className="bg-slate-100 rounded-xl p-4 text-center">
                <p className="text-sm text-slate-600 mb-2">
                  Heb je vragen over het BPV Protocol? Vraag het aan Sahbi AI!
                </p>
                <Button
                  onClick={onOpenChat}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Vraag Sahbi AI
                </Button>
              </div>
            </div>
          )}

          {/* Gesprekken Tab */}
          {activeTab === 'gesprekken' && (
            <div className="space-y-6">
              {/* Add Button */}
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-slate-800">Voortgangsgesprekken</h3>
                <Button
                  onClick={() => setShowAddGesprek(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CalendarPlus className="w-4 h-4 mr-1" />
                  Plan Gesprek
                </Button>
              </div>

              {/* Gesprekken List */}
              {gesprekken.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">Nog geen gesprekken gepland</p>
                  <p className="text-sm text-slate-500 mb-4">Plan je eerste voortgangsgesprek</p>
                  <Button onClick={() => setShowAddGesprek(true)} variant="outline">
                    <Plus className="w-4 h-4 mr-1" />
                    Plan Gesprek
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {gesprekken.map((gesprek) => (
                    <div 
                      key={gesprek.id}
                      className={`border rounded-xl p-4 transition-colors ${
                        gesprek.status === 'voltooid' 
                          ? 'bg-green-50 border-green-200'
                          : gesprek.status === 'geannuleerd'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-800">
                              {gesprekTypes.find(t => t.value === gesprek.type)?.label || gesprek.type}
                            </span>
                            <StatusBadge status={gesprek.status} />
                          </div>
                          <div className="text-sm text-slate-600 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {gesprek.datum} om {gesprek.tijd}
                          </div>
                          <div className="text-sm text-slate-600 flex items-center gap-2 mt-1">
                            <User className="w-4 h-4" />
                            {gesprek.met_wie}
                          </div>
                          {gesprek.notities && (
                            <p className="text-sm text-slate-500 mt-2 bg-slate-100 p-2 rounded">
                              {gesprek.notities}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {gesprek.status === 'gepland' && (
                            <button
                              onClick={() => handleUpdateGesprek({ ...gesprek, status: 'voltooid' })}
                              className="p-2 hover:bg-green-100 rounded-lg text-green-600 transition-colors"
                              title="Markeer als voltooid"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setNewGesprek({
                                datum: gesprek.datum,
                                tijd: gesprek.tijd,
                                type: gesprek.type,
                                met_wie: gesprek.met_wie,
                                notities: gesprek.notities || ''
                              });
                              setShowEditGesprek(gesprek.id);
                            }}
                            className="p-2 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteGesprek(gesprek.id)}
                            className="p-2 hover:bg-red-50 rounded-lg text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contacten Tab */}
          {activeTab === 'contacten' && (
            <div>
              <h3 className="font-semibold text-slate-800 mb-4">Wie kan je helpen?</h3>
              <div className="space-y-3">
                {inwerkData.contacten?.map((contact) => (
                  <div 
                    key={contact.naam}
                    className="bg-slate-50 rounded-xl p-4 border border-slate-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">{contact.rol}</div>
                        <div className="font-medium text-slate-800">{contact.naam}</div>
                        <div className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {contact.bereikbaar}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Urenregistratie Tab */}
          {activeTab === 'uren' && (
            <div className="space-y-6">
              {/* Header met totaal */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">Urenregistratie</h3>
                  <p className="text-sm text-slate-500">Registreer je gewerkte uren</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">{totaalUren}</div>
                    <div className="text-xs text-slate-500">totaal uren</div>
                  </div>
                  <Button 
                    onClick={() => setShowAddUren(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Uren toevoegen
                  </Button>
                </div>
              </div>

              {/* Uren overzicht */}
              {urenRegistraties.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Nog geen uren geregistreerd</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setShowAddUren(true)}
                  >
                    Eerste registratie toevoegen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {urenRegistraties.map((registratie) => (
                    <div 
                      key={registratie.id}
                      className="bg-white rounded-xl p-4 border border-slate-200 hover:border-purple-200 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-slate-800">
                              {new Date(registratie.datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
                            </span>
                            <StatusBadge status={registratie.status} />
                          </div>
                          <div className="text-sm text-slate-600 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {registratie.start_tijd} - {registratie.eind_tijd}
                            </span>
                            <span className="font-medium text-purple-600">
                              {registratie.gewerkt_uren} uur
                            </span>
                            <span className="text-slate-400">|</span>
                            <span>{registratie.locatie}</span>
                          </div>
                          <p className="text-sm text-slate-500 mt-2">{registratie.werkzaamheden}</p>
                        </div>
                        {registratie.status === 'ingediend' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteUren(registratie.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Ziekmeldingen Tab */}
          {activeTab === 'ziekmeldingen' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800">Ziekmeldingen</h3>
                  <p className="text-sm text-slate-500">Meld je ziek of beter</p>
                </div>
                <Button 
                  onClick={() => setShowAddZiekmelding(true)}
                  className="bg-amber-500 hover:bg-amber-600"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Ziek melden
                </Button>
              </div>

              {/* Actieve ziekmelding */}
              {ziekmeldingen.filter(z => z.status === 'actief').length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    Actieve ziekmelding
                  </div>
                  {ziekmeldingen.filter(z => z.status === 'actief').map((ziekmelding) => (
                    <div key={ziekmelding.id} className="space-y-2">
                      <p className="text-sm text-slate-600">
                        Ziek sinds: <span className="font-medium">{new Date(ziekmelding.start_datum).toLocaleDateString('nl-NL')}</span>
                      </p>
                      {ziekmelding.verwachte_eind_datum && (
                        <p className="text-sm text-slate-600">
                          Verwacht herstel: <span className="font-medium">{new Date(ziekmelding.verwachte_eind_datum).toLocaleDateString('nl-NL')}</span>
                        </p>
                      )}
                      <Button 
                        onClick={() => handleHerstelmelding(ziekmelding.id)}
                        className="bg-green-600 hover:bg-green-700 mt-2"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Ik ben hersteld
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Ziekmeldingen geschiedenis */}
              <div>
                <h4 className="font-medium text-slate-700 mb-3">Geschiedenis</h4>
                {ziekmeldingen.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <CheckCircle className="w-10 h-10 mx-auto text-green-400 mb-2" />
                    <p className="text-slate-500">Geen ziekmeldingen</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ziekmeldingen.map((ziekmelding) => (
                      <div 
                        key={ziekmelding.id}
                        className={`rounded-lg p-3 border ${
                          ziekmelding.status === 'actief' 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium">
                              {new Date(ziekmelding.start_datum).toLocaleDateString('nl-NL')}
                              {ziekmelding.eind_datum && (
                                <span className="text-slate-400"> - {new Date(ziekmelding.eind_datum).toLocaleDateString('nl-NL')}</span>
                              )}
                            </span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            ziekmelding.status === 'actief' 
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {ziekmelding.status === 'actief' ? 'Actief' : 'Hersteld'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Evaluaties Tab */}
          {activeTab === 'evaluaties' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-slate-800">Mijn Evaluaties</h3>
                <p className="text-sm text-slate-500">Bekijk je beoordelingen en feedback</p>
              </div>

              {evaluaties.length === 0 ? (
                <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <Star className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Nog geen evaluaties</p>
                  <p className="text-sm text-slate-400 mt-1">Je werkbegeleider zal hier je beoordelingen toevoegen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {evaluaties.map((evaluatie) => (
                    <div 
                      key={evaluatie.id}
                      className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800 capitalize">{evaluatie.type} evaluatie</span>
                            <span className="text-xs text-slate-400">
                              {new Date(evaluatie.datum).toLocaleDateString('nl-NL')}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">Beoordeeld door: {evaluatie.beoordelaar}</p>
                        </div>
                        <div className="flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                          <Star className="w-4 h-4 fill-current" />
                          <span className="font-bold">{evaluatie.gemiddelde_score}</span>
                        </div>
                      </div>

                      {/* Scores */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        {Object.entries(evaluatie.scores || {}).map(([key, value]) => (
                          <div key={key} className="bg-slate-50 rounded-lg p-2">
                            <div className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</div>
                            <div className="font-semibold text-slate-800">{value}/10</div>
                          </div>
                        ))}
                      </div>

                      {/* Feedback */}
                      <div className="space-y-3">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <div className="text-xs font-medium text-slate-500 mb-1">Feedback</div>
                          <p className="text-sm text-slate-700">{evaluatie.feedback}</p>
                        </div>
                        {evaluatie.sterke_punten && (
                          <div className="bg-green-50 rounded-lg p-3">
                            <div className="text-xs font-medium text-green-600 mb-1 flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" /> Sterke punten
                            </div>
                            <p className="text-sm text-slate-700">{evaluatie.sterke_punten}</p>
                          </div>
                        )}
                        {evaluatie.verbeterpunten && (
                          <div className="bg-amber-50 rounded-lg p-3">
                            <div className="text-xs font-medium text-amber-600 mb-1 flex items-center gap-1">
                              <Target className="w-3 h-3" /> Verbeterpunten
                            </div>
                            <p className="text-sm text-slate-700">{evaluatie.verbeterpunten}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add Uren Modal */}
      <Dialog open={showAddUren} onOpenChange={setShowAddUren}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-600" />
              Uren Registreren
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Datum</label>
              <Input
                type="date"
                value={newUren.datum}
                onChange={(e) => setNewUren({ ...newUren, datum: e.target.value })}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Starttijd</label>
                <Input
                  type="time"
                  value={newUren.start_tijd}
                  onChange={(e) => setNewUren({ ...newUren, start_tijd: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Eindtijd</label>
                <Input
                  type="time"
                  value={newUren.eind_tijd}
                  onChange={(e) => setNewUren({ ...newUren, eind_tijd: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Pauze (minuten)</label>
              <Input
                type="number"
                value={newUren.pauze_minuten}
                onChange={(e) => setNewUren({ ...newUren, pauze_minuten: parseInt(e.target.value) || 0 })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Locatie</label>
              <select
                value={newUren.locatie}
                onChange={(e) => setNewUren({ ...newUren, locatie: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="De Zwaluw">De Zwaluw</option>
                <option value="De Adelaars">De Adelaars</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Werkzaamheden</label>
              <textarea
                value={newUren.werkzaamheden}
                onChange={(e) => setNewUren({ ...newUren, werkzaamheden: e.target.value })}
                placeholder="Beschrijf kort je werkzaamheden..."
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[80px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Opmerkingen (optioneel)</label>
              <Input
                value={newUren.opmerkingen}
                onChange={(e) => setNewUren({ ...newUren, opmerkingen: e.target.value })}
                placeholder="Bijzonderheden..."
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddUren(false)}>
                Annuleren
              </Button>
              <Button onClick={handleAddUren} className="bg-purple-600 hover:bg-purple-700">
                <Save className="w-4 h-4 mr-1" />
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Ziekmelding Modal */}
      <Dialog open={showAddZiekmelding} onOpenChange={setShowAddZiekmelding}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Ziek Melden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <strong>Let op:</strong> Bel ook altijd je werkbegeleider om je ziek te melden!
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Datum eerste ziektedag</label>
              <Input
                type="date"
                value={newZiekmelding.start_datum}
                onChange={(e) => setNewZiekmelding({ ...newZiekmelding, start_datum: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Verwachte hersteldatum (optioneel)</label>
              <Input
                type="date"
                value={newZiekmelding.verwachte_eind_datum}
                onChange={(e) => setNewZiekmelding({ ...newZiekmelding, verwachte_eind_datum: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Reden (optioneel)</label>
              <Input
                value={newZiekmelding.reden}
                onChange={(e) => setNewZiekmelding({ ...newZiekmelding, reden: e.target.value })}
                placeholder="Je hoeft geen details te geven"
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="contact_opgenomen"
                checked={newZiekmelding.contact_opgenomen}
                onChange={(e) => setNewZiekmelding({ ...newZiekmelding, contact_opgenomen: e.target.checked })}
                className="rounded border-slate-300"
              />
              <label htmlFor="contact_opgenomen" className="text-sm text-slate-700">
                Ik heb mijn werkbegeleider gebeld
              </label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddZiekmelding(false)}>
                Annuleren
              </Button>
              <Button onClick={handleAddZiekmelding} className="bg-amber-500 hover:bg-amber-600">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Ziek melden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Leerdoel Modal */}
      <Dialog open={showAddLeerdoel} onOpenChange={setShowAddLeerdoel}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Nieuw Leerdoel Toevoegen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Leerdoel</label>
              <Input
                value={newLeerdoel.doel}
                onChange={(e) => setNewLeerdoel({ ...newLeerdoel, doel: e.target.value })}
                placeholder="Bijv. Ik kan zelfstandig een rapport schrijven"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Categorie</label>
              <select
                value={newLeerdoel.categorie}
                onChange={(e) => setNewLeerdoel({ ...newLeerdoel, categorie: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                {categorieOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddLeerdoel(false)}>
                Annuleren
              </Button>
              <Button onClick={handleAddLeerdoel} className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-1" />
                Toevoegen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Leerdoel Modal */}
      <Dialog open={!!showEditLeerdoel} onOpenChange={() => setShowEditLeerdoel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Leerdoel Bewerken
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Leerdoel</label>
              <Input
                value={editLeerdoel.doel}
                onChange={(e) => setEditLeerdoel({ ...editLeerdoel, doel: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Status</label>
              <select
                value={editLeerdoel.status}
                onChange={(e) => setEditLeerdoel({ ...editLeerdoel, status: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="nieuw">Nieuw</option>
                <option value="bezig">Bezig</option>
                <option value="afgerond">Afgerond</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Reflectie</label>
              <textarea
                value={editLeerdoel.reflectie}
                onChange={(e) => setEditLeerdoel({ ...editLeerdoel, reflectie: e.target.value })}
                placeholder="Wat heb je geleerd? Wat ging goed/minder goed?"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[80px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Bewijs/Voorbeelden</label>
              <textarea
                value={editLeerdoel.bewijs}
                onChange={(e) => setEditLeerdoel({ ...editLeerdoel, bewijs: e.target.value })}
                placeholder="Welke voorbeelden of bewijs heb je verzameld?"
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[60px]"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowEditLeerdoel(null)}>
                Annuleren
              </Button>
              <Button onClick={handleUpdateLeerdoel} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-1" />
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Gesprek Modal */}
      <Dialog open={showAddGesprek} onOpenChange={setShowAddGesprek}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-blue-600" />
              Gesprek Plannen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Type gesprek</label>
              <select
                value={newGesprek.type}
                onChange={(e) => setNewGesprek({ ...newGesprek, type: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {gesprekTypes.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum</label>
                <Input
                  type="date"
                  value={newGesprek.datum}
                  onChange={(e) => setNewGesprek({ ...newGesprek, datum: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd</label>
                <Input
                  type="time"
                  value={newGesprek.tijd}
                  onChange={(e) => setNewGesprek({ ...newGesprek, tijd: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Met wie?</label>
              <Input
                value={newGesprek.met_wie}
                onChange={(e) => setNewGesprek({ ...newGesprek, met_wie: e.target.value })}
                placeholder="Bijv. Werkbegeleider, School"
                className="mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowAddGesprek(false)}>
                Annuleren
              </Button>
              <Button onClick={handleAddGesprek} className="bg-blue-600 hover:bg-blue-700">
                <CalendarPlus className="w-4 h-4 mr-1" />
                Plannen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Gesprek Modal */}
      <Dialog open={!!showEditGesprek} onOpenChange={() => setShowEditGesprek(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Gesprek Bewerken
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Type gesprek</label>
              <select
                value={newGesprek.type}
                onChange={(e) => setNewGesprek({ ...newGesprek, type: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {gesprekTypes.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Datum</label>
                <Input
                  type="date"
                  value={newGesprek.datum}
                  onChange={(e) => setNewGesprek({ ...newGesprek, datum: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Tijd</label>
                <Input
                  type="time"
                  value={newGesprek.tijd}
                  onChange={(e) => setNewGesprek({ ...newGesprek, tijd: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Met wie?</label>
              <Input
                value={newGesprek.met_wie}
                onChange={(e) => setNewGesprek({ ...newGesprek, met_wie: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Notities</label>
              <textarea
                value={newGesprek.notities || ''}
                onChange={(e) => setNewGesprek({ ...newGesprek, notities: e.target.value })}
                placeholder="Voeg notities toe na het gesprek..."
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[80px]"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowEditGesprek(null)}>
                Annuleren
              </Button>
              <Button onClick={() => handleUpdateGesprek(newGesprek)} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-1" />
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Opdracht Modal */}
      <Dialog open={showUploadOpdracht} onOpenChange={setShowUploadOpdracht}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-600" />
              Opdracht Uploaden
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Titel *</label>
              <Input
                value={newOpdracht.titel}
                onChange={(e) => setNewOpdracht({ ...newOpdracht, titel: e.target.value })}
                placeholder="Bijv. Weekreflectie Week 3"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Beschrijving</label>
              <textarea
                value={newOpdracht.beschrijving}
                onChange={(e) => setNewOpdracht({ ...newOpdracht, beschrijving: e.target.value })}
                placeholder="Korte beschrijving van de opdracht..."
                className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 min-h-[80px]"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Bestand (PDF of Word) *</label>
              <div className="mt-1">
                <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
                  <div className="text-center">
                    {newOpdracht.bestand ? (
                      <>
                        <FileText className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                        <p className="text-sm text-slate-700 font-medium">{newOpdracht.bestand.name}</p>
                        <p className="text-xs text-slate-500">{(newOpdracht.bestand.size / 1024 / 1024).toFixed(2)} MB</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">Klik om bestand te selecteren</p>
                        <p className="text-xs text-slate-400">PDF of Word, max 10MB</p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => setNewOpdracht({ ...newOpdracht, bestand: e.target.files[0] })}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
                setShowUploadOpdracht(false);
                setNewOpdracht({ titel: '', beschrijving: '', bestand: null });
              }}>
                Annuleren
              </Button>
              <Button 
                onClick={handleUploadOpdracht} 
                disabled={uploadLoading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {uploadLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Uploaden...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-1" />
                    Indienen
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hulp Nodig Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-slate-800">Hulp nodig met je stage?</h4>
              <p className="text-sm text-slate-600">Vraag Sahbi AI voor tips en begeleiding</p>
            </div>
          </div>
          <Button 
            onClick={onOpenChat}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Chat starten
          </Button>
        </div>
      </div>
    </div>
  );
}
